import React, { createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials, logout as reduxLogout } from '../store/slices/authSlice';
import { RootState } from '../store';
import { AuthService } from '../services/auth.service';

interface User {
    id: string;
    email: string;
}

interface AuthContextType {
    user: User | null;
    login: (token: string, user: User) => void;
    logout: () => void;
    getCurrentUser: () => User | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const existingAuth = useSelector((state: RootState) => state.auth);

    // On mount, check session via AuthService using cookies
    React.useEffect(() => {
        const checkSession = async () => {
            try {
                const response = await AuthService.checkSession();
                console.log('DEBUG: checkSession response:', response); 
                console.log('DEBUG: response.data:', response.data);
                console.log('DEBUG: response.data.user:', response.data?.user);
                if (response.success && response.data && response.data.user) {
                    const userData = { id: response.data.user.id, email: response.data.user.email };
                    setUser(userData);
                    
                    // CRITICAL FIX: Only update Redux auth if it's not already populated from persistence
                    // This prevents overwriting persisted workspace/role data on page reload
                    if (!existingAuth.isAuthenticated || !existingAuth.user) {
                        dispatch(setCredentials({ 
                            user: userData, 
                            isAuthenticated: true,
                            // Include data from session response
                            role_id: response.data.role_id,
                            permissions: response.data.permissions,
                            team_id: response.data.team_id,
                            activeWorkspaceId: response.data.activeWorkspaceId
                        }));
                    } else {
                        // Just update the user data and ensure isAuthenticated is true
                        dispatch(setCredentials({
                            ...existingAuth,
                            user: userData,
                            isAuthenticated: true
                        }));
                    }
                } else {
                    setUser(null);
                    dispatch(reduxLogout());
                }
            } catch {
                setUser(null);
                dispatch(reduxLogout());
            } finally {
                setLoading(false);
            }
        };
        
        // CRITICAL: Listen for extension login sync messages
        const handleExtensionMessages = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return; // Security check
            }
            
            if (event.data && event.data.type === 'EXTENSION_LOGIN_SYNC') {
                console.log('AuthContext: Extension login sync received', event.data.payload);
                
                const authData = event.data.payload;
                
                // Import and restore encryption keys
                import('../utils/secureStore').then(({ secureStore }) => {
                    if (authData.encryptionKey && authData.symmetricKey && authData.vaultKey) {
                        secureStore.setKeys(
                            authData.encryptionKey,
                            authData.symmetricKey,
                            authData.vaultKey
                        );
                        console.log('AuthContext: Encryption keys restored from extension login');
                    }
                });
                
                // Update Redux state with complete auth data
                dispatch(setCredentials({
                    user: authData.user,
                    isAuthenticated: authData.isAuthenticated,
                    role_id: authData.role_id,
                    permissions: authData.permissions,
                    team_id: authData.team_id,
                    activeWorkspaceId: authData.activeWorkspaceId
                }));
                
                // Update local user state
                setUser(authData.user);
                
                console.log('AuthContext: Extension login sync completed successfully');
            }
            
            // Handle extension logout
            if (event.data && event.data.type === 'EXTENSION_LOGOUT_SYNC') {
                console.log('AuthContext: Extension logout sync received');
                
                // Clear all auth data
                setUser(null);
                dispatch(reduxLogout());
                
                // Clear encryption keys
                import('../utils/secureStore').then(({ secureStore }) => {
                    secureStore.clearKeys();
                });
                
                // Optionally redirect to login page
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                
                console.log('AuthContext: Extension logout sync completed');
            }
        };
        
        window.addEventListener('message', handleExtensionMessages);
        
        checkSession();
        
        // Cleanup
        return () => {
            window.removeEventListener('message', handleExtensionMessages);
        };
    }, [dispatch]);

    const login = (_token: string, userData: User) => {
        setUser(userData);
        dispatch(setCredentials({ user: userData, isAuthenticated: true }));
    };

    const logout = async () => {
        try {
            await AuthService.logout();
        } catch {}
        setUser(null);
        dispatch(reduxLogout());
    };

    const getCurrentUser = () => user;

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <AuthContext.Provider value={{ user, login, logout, getCurrentUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};